import { type JSX, type ReactNode, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import { submitExperienceFeedback } from '../api/feedback.ts';
import { useTokenStore } from '../api/use-api.ts';
import { failureMessage } from '../lib/error-labels.ts';
import { cn } from '../lib/utils.ts';
import { Textarea, buttonClass } from './ui/index.ts';

const RATINGS = [1, 2, 3, 4, 5] as const;

/**
 * The three-question experience form. The rating is the only required answer, so it takes
 * one tap. Shared by the Help tab and the monthly pop-up so the two never drift apart.
 */
export function ExperienceFeedbackForm({
  idPrefix,
  onSubmitted,
  secondaryActions,
}: {
  idPrefix: string;
  onSubmitted: () => void;
  secondaryActions?: ReactNode;
}): JSX.Element {
  const store = useTokenStore();
  const [rating, setRating] = useState<number | null>(null);
  const [wentWell, setWentWell] = useState('');
  const [couldBeBetter, setCouldBeBetter] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: (r: number) =>
      submitExperienceFeedback({ rating: r, wentWell: wentWell.trim(), couldBeBetter: couldBeBetter.trim() }, store),
    onSuccess: () => {
      setRating(null);
      setWentWell('');
      setCouldBeBetter('');
      setError(null);
      onSubmitted();
    },
    onError: (e) => setError(failureMessage(e, 'Your feedback could not be sent. Please try again.')),
  });

  return (
    <form
      className="space-y-4"
      data-testid="experience-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (rating === null) { setError('Please choose a rating from 1 to 5.'); return; }
        submit.mutate(rating);
      }}
    >
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">How do you rate your experience of HapMetrix?</legend>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Rating from 1 (poor) to 5 (excellent)">
          {RATINGS.map((r) => (
            <button
              key={r}
              type="button"
              role="radio"
              aria-checked={rating === r}
              data-testid={`experience-rating-${String(r)}`}
              onClick={() => { setRating(r); setError(null); }}
              className={cn(
                'h-11 w-11 rounded-md border text-sm font-semibold',
                rating === r
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-background text-foreground hover:bg-accent',
              )}
            >
              {r}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">1 is poor, 5 is excellent.</p>
      </fieldset>

      <div className="space-y-1">
        <label htmlFor={`${idPrefix}-went-well`} className="text-sm font-medium">
          What is working well for you? <span className="font-normal text-muted-foreground">(optional)</span>
        </label>
        <Textarea
          id={`${idPrefix}-went-well`}
          data-testid="experience-went-well"
          rows={3}
          maxLength={2000}
          value={wentWell}
          onChange={(e) => setWentWell(e.target.value)}
          className="resize-y"
        />
      </div>

      <div className="space-y-1">
        <label htmlFor={`${idPrefix}-could-be-better`} className="text-sm font-medium">
          What could be better? <span className="font-normal text-muted-foreground">(optional)</span>
        </label>
        <Textarea
          id={`${idPrefix}-could-be-better`}
          data-testid="experience-could-be-better"
          rows={3}
          maxLength={2000}
          value={couldBeBetter}
          onChange={(e) => setCouldBeBetter(e.target.value)}
          className="resize-y"
        />
      </div>

      <p className="text-xs text-muted-foreground">
        Please don&apos;t include details that identify a client.
      </p>

      {error && (
        <div role="alert" data-testid="experience-error" className="flex items-start gap-2.5 rounded-lg border border-destructive/30 bg-destructive/5 px-3.5 py-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" data-testid="experience-submit" disabled={submit.isPending} className={buttonClass('primary')}>
          {submit.isPending ? 'Sending…' : 'Send feedback'}
        </button>
        {secondaryActions}
      </div>
    </form>
  );
}
